import type { Call, Device } from "@twilio/voice-sdk";
import type { VoiceCall } from "~/modules/VoiceCall";
import { VoiceCallImpl } from "~/modules/VoiceCall";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { Action } from "~/modules/actions/Action";
import { isSupervisorAlreadyMonitoringCallTask } from "~/modules/actions/ActionUtils";
import { CbmImpl } from "~/modules/cbm/CbmImpl";




export interface MonitorCallOptions {
    extraParams?: Record<any, any>;
    voiceDevice?: Device;
}





















export class MonitorCall implements Action<Promise<VoiceCall>> {
    


    #taskSid: string;

    


    #reservationSid: string;

    


    #options: MonitorCallOptions = {};

    constructor(taskSid: string, reservationSid: string, options: MonitorCallOptions = {}) {
        this.#taskSid = taskSid;
        this.#reservationSid = reservationSid;
        this.#options = options;
    }

    async run(ctx: {}): Promise<VoiceCall> {
        const context = ctx as ContextManager;
        const taskRouter = context.getInstanceOf(TaskRouterImpl);
        const voiceController = context.getInstanceOf(VoiceControllerImpl);
        const logger = getLogger(context)("MonitorCall");
        const cbm = context.getInstanceOf(CbmImpl);

        logger.debug(`monitorCall invoked with taskSid: ${this.#taskSid}`);

        const worker = taskRouter.worker;
        const task = await taskRouter.workspace.fetchTask(this.#taskSid);

        if (!worker) {
            const errorMsg = "monitorCall: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (await isSupervisorAlreadyMonitoringCallTask(task, worker, cbm)) {
            const errorMsg = `monitorCall: supervisor is already monitoring call for taskSid: ${this.#taskSid}`;
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        await this.#initVoiceController(context);

        try {
            await taskRouter.worker.monitor(
                this.#taskSid,
                this.#reservationSid,
                this.#options.extraParams ? { ...this.#options.extraParams } : {}
            );
        } catch (error) {
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, error.message));
        }

        return new Promise((resolve) => {
            const handleIncomingCall = (call: Call) => {
                logger.info("Incoming call event received in monitorCall");

                const voiceCall = new VoiceCallImpl(context, call, voiceController.voiceDevice);

                voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);

                resolve(voiceCall);
            };
            voiceController.subscribeToIncomingCallEvent(handleIncomingCall);
        });
    }

    


    #initVoiceController = async (context: ContextManager) => {
        const voiceDevice = this.#options.voiceDevice;
        const logger = getLogger(context)("StartOutboundCall");
        const voiceController = context.getInstanceOf(VoiceControllerImpl);
        try {
            if (!voiceController.isInitialized) {
                await voiceController.init(voiceDevice);
            }
        } catch (e) {
            logger.error("Failed to initialise VoiceController", e);
        }
    };
}
